import { useState, useEffect, useCallback } from 'react';

/**
 * Run-mode widget chrome: show header/footer on click, hide on outside click,
 * blur, or Escape.
 */
export function useWidgetChrome(isEnabled) {
  const [openWidgetId, setOpenWidgetId] = useState(null);

  useEffect(() => {
    if (!isEnabled || openWidgetId == null) {
      return undefined;
    }

    const handlePointerDown = (event) => {
      const root = document.querySelector(
        `[data-widget-chrome-id="${openWidgetId}"]`
      );
      if (root && !root.contains(event.target)) {
        setOpenWidgetId(null);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [isEnabled, openWidgetId]);

  const isChromeVisible = useCallback(
    (widgetId) => isEnabled && openWidgetId === widgetId,
    [isEnabled, openWidgetId]
  );

  const handleContentClick = useCallback(
    (widgetId) => (event) => {
      if (!isEnabled) return;
      event.stopPropagation();
      setOpenWidgetId(widgetId);
    },
    [isEnabled]
  );

  const handleWidgetBlur = useCallback(
    (widgetId) => (event) => {
      if (!isEnabled || openWidgetId !== widgetId) return;
      if (!event.currentTarget.contains(event.relatedTarget)) {
        setOpenWidgetId(null);
      }
    },
    [isEnabled, openWidgetId]
  );

  const handleWidgetKeyDown = useCallback(
    (event) => {
      if (event.key === 'Escape') {
        setOpenWidgetId(null);
      }
    },
    []
  );

  const hideChrome = useCallback(() => setOpenWidgetId(null), []);

  return {
    isChromeVisible,
    handleContentClick,
    handleWidgetBlur,
    handleWidgetKeyDown,
    hideChrome,
  };
}

export default useWidgetChrome;
